import { useUser } from "@dashboard/auth";
import { iconSize } from "@dashboard/components/icons";
import { useAppsAlert } from "@dashboard/extensions/components/AppAlerts/useAppsAlert";
import { extensionMountPoints } from "@dashboard/extensions/extensionMountPoints";
import { useExtensions } from "@dashboard/extensions/hooks/useExtensions";
import { PermissionEnum } from "@dashboard/graphql";
import { CustomersIcon } from "@dashboard/icons/Customers";
import { HomeIcon } from "@dashboard/icons/Home";
// import ModelingIcon from "@dashboard/icons/Modeling";
import { OrdersIcon } from "@dashboard/icons/Orders";
import { ProductsIcon } from "@dashboard/icons/Products";
import { sectionNames } from "@dashboard/intl";
// import { ripplePagesAreModels } from "@dashboard/modeling/ripples/pagesAreModels";
// import { Ripple } from "@dashboard/ripples/components/Ripple";
import { Box } from "@saleor/macaw-ui-next";
import { Settings, Truck } from "lucide-react";
import * as React from "react";
import { useIntl } from "react-intl";

import { SidebarMenuItem } from "../types";
import { mapToExtensionsItems } from "../utils";

export function useMenuStructure() {
  useAppsAlert();

  const extensions = useExtensions(extensionMountPoints.NAVIGATION_SIDEBAR);
  const intl = useIntl();
  const { user } = useUser();

  const appExtensionsHeaderItem: SidebarMenuItem = {
    id: "extensions",
    label: intl.formatMessage(sectionNames.appExtensions),
    type: "divider",
    paddingY: 1.5,
  };

  const menuItems: SidebarMenuItem[] = [
    {
      icon: renderIcon(<HomeIcon />),
      label: "Home",
      id: "home",
      url: "/", // Changed from "/reports" to "/"
      type: "item",
      // Add matchUrls to ensure it's highlighted on the root path
      matchUrls: ["/", "/dashboard", ""],
    },
    {
      children: [
        {
          label: "Products List",
          id: "products-list",
          url: "/products/",
          type: "item",
        },
        {
          label: "Category List",
          id: "categories-list",
          url: "/categories/",
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_CATALOG, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<ProductsIcon />),
      label: "Catalog",
      permissions: [PermissionEnum.MANAGE_PRODUCTS],
      id: "catalog",
      type: "itemGroup",
    },
    {
      children: [
        {
          label: "Customer List",
          id: "customer-list",
          url: "/buyers",
          type: "item",
        },
        {
          label: "Sale Person List",
          id: "sale-person-list",
          url: "/staff/?permissionGroup=Sale%20Person&asc=true&sort=name",
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_CUSTOMERS, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<CustomersIcon />),
      label: "Users",
      permissions: [PermissionEnum.MANAGE_USERS, PermissionEnum.MANAGE_STAFF],
      id: "users",
      type: "itemGroup",
    },
    {
      children: [
        {
          label: "Sales by Direact Warehouse",
          id: "direct-sale",
          url: "/orders?asc=false&sort=number&pdc=1",
          type: "item",
          matchUrls: ["/orders?asc=false&sort=number&pdc=1"],
        },
        {
          label: "Sales Report",
          id: "sales-reports",
          url: "/reports",
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_ORDERS, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<OrdersIcon />),
      label: "Sales",
      permissions: [PermissionEnum.MANAGE_ORDERS],
      id: "sales",
      type: "itemGroup",
    },
    {
      children: [
        {
          label: "Vendor List",
          id: "vendor-list",
          url: "/wholesaler",
          type: "item",
        },
        {
          label: "Generate PO",
          id: "new-generate-po",
          url: "/wholesaler-generate-po",
          type: "item",
        },
        {
          label: "History",
          id: "po-history",
          url: "/history",
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_ORDERS, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<Truck size={iconSize.small} />),
      label: "Vendors",
      permissions: [PermissionEnum.MANAGE_ORDERS],
      id: "vendors",
      type: "itemGroup",
    },
    {
      children: [
        {
          label: "Collection",
          id: "collection",
          url: "/collections/?asc=true&sort=name",
          type: "item",
        },
        {
          label: "Configuration",
          id: "configuration",
          url: "/configuration/",
          type: "item",
        },
        {
          label: "Translations",
          id: "translations-system",
          url: "/translations/",
          type: "item",
        },
        {
          label: "Sales person Report",
          id: "sales-person-report",
          url: "/customer-reports",
          type: "item",
        },
        {
          label: "Product Report",
          id: "product-report-system",
          url: "/product-reports",
          type: "item",
        },
        {
          label: "Wholesaler Orders",
          id: "wholesaler-orders-system",
          url: "/wholesaler-orders",
          type: "item",
        },
        {
          label: "Vendors Placed Orders",
          id: "generate-po",
          url: "/wholesaler-placed-orders",
          type: "item",
        },
        {
          label: "Pages",
          id: "pages",
          url: "/models/?asc=true&sort=title",
          type: "item",
        },
        {
          label: "Page Type",
          id: "page-type",
          url: "/model-types/?asc=true&sort=name",
          type: "item",
        },
        {
          label: "Navigation",
          id: "navigation",
          url: "/structures?asc=true&sort=name",
          type: "item",
        },
        {
          label: "Sales by Sales-Persons",
          id: "sale-persons-sales",
          url: "/orders?asc=false&sort=number&pdc=0",
          type: "item",
          matchUrls: ["/orders?asc=false&sort=number&pdc=0"],
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_TRANSLATIONS, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<Settings size={iconSize.small} />),
      label: "System",
      permissions: [PermissionEnum.MANAGE_SETTINGS],
      id: "system",
      type: "itemGroup",
    },
    // {
    //   children: [
    //     {
    //       label: "Pages",
    //       id: "pages",
    //       url: "/models/?asc=true&sort=title",
    //       type: "item",
    //     },
    //     {
    //       label: "Page Type",
    //       id: "page-type",
    //       url: "/model-types/?asc=true&sort=name",
    //       type: "item",
    //     },
    //     {
    //       label: "Navigation",
    //       id: "navigation",
    //       url: "/structures?asc=true&sort=name",
    //       type: "item",
    //     },
    //     ...mapToExtensionsItems(extensions.NAVIGATION_PAGES, appExtensionsHeaderItem),
    //   ],
    //   icon: renderIcon(<ModelingIcon />),
    //   label: "Modeling",
    //   permissions: [PermissionEnum.MANAGE_PAGES, PermissionEnum.MANAGE_MENUS],
    //   id: "modeling",
    //   endAdornment: <Ripple model={ripplePagesAreModels} />,
    //   type: "itemGroup",
    // },
  ];

  const isMenuItemPermitted = (menuItem: SidebarMenuItem) => {
    const userPermissions = (user?.userPermissions || []).map(permission => permission.code);

    if (!menuItem?.permissions || menuItem?.permissions?.length < 1) {
      return true;
    }

    return menuItem.permissions.some(permission => userPermissions.includes(permission));
  };

  const getFilteredMenuItems = (menuItems: SidebarMenuItem[]) =>
    menuItems.filter(isMenuItemPermitted);

  return menuItems.reduce((resultItems: SidebarMenuItem[], menuItem: SidebarMenuItem) => {
    if (!isMenuItemPermitted(menuItem)) {
      return resultItems;
    }

    const { children } = menuItem;
    const filteredChildren = children ? getFilteredMenuItems(children) : undefined;

    return [...resultItems, { ...menuItem, children: filteredChildren }];
  }, []);
}

function renderIcon(icon: React.ReactNode) {
  return (
    <Box color="default2" __width={20} __height={20}>
      {icon}
    </Box>
  );
}
